import nodelistToArray from '@javascripts/utils/nodelist_to_array';
import isFeatureToggleOn from "@javascripts/utils/feature_toggle";
import localStorageAdapter from '@javascripts/adapters/localstorage_adapter';
import i18n from '@javascripts/utils/i18n';

export default class SubtitleManager {
  constructor(player, options = {}) {
    this._player = player;
    this._options = options;
  }

  setActiveSubtitle(activeSubtitle){
    const activeSub = activeSubtitle === "" ? "none" : activeSubtitle
    if (window["playback_active_subtitle"]){
      window["playback_active_subtitle"][this.player.id()] = activeSub
    } else {
      window["playback_active_subtitle"] = {
        [this.player.id()]: activeSub
      }
    }
  }

  storageName() {
    return 'preferred_lang_subs'
  }

  get player() {
    return this._player;
  }

  get defaultLang() {
    return 'indonesian';
  }

  preferredLang() {
    const data = localStorageAdapter().getItem(this.storageName());
    return data || this.defaultLang
  }

  textTracks() {
    return nodelistToArray(this.player.textTracks()).filter((track) => {
      return !this.trackIsMetadata(track) && !this.trackIsAdCues(track);
    });
  }

  hasSubtitle() {
    return this.textTracks().length > 0;
  }

  getActiveSubtitle() {
    if(this.hasSubtitle()) {
      const activeSub = this.textTracks().find(track => track.mode === 'showing');
      return activeSub ? activeSub : {
        label: i18n("settings_menu.off")
      }
    } else {
      return {
        label: ""
      }
    }
  }

  getSubtitleMenu() {
    const activeSubs = this.getActiveSubtitle();

    return [
      ...this.textTracks()
        .filter((track) => {
          return this.trackHasProperKind(track);
        })
        .map((track) => {
          return {
            track,
            label: track.label,
            active: activeSubs?.label === track.label,
            onClick: this.createTrackChangeHandler(track)
          }
        }),
      {
        track: null,
        label: 'Off',
        active: (!activeSubs || this.getPreferredLang() === 'off'),
        onClick: this.handleSubtitleOff.bind(this)
      },
    ];
  }

  createTrackChangeHandler(track) {
    return () => this.handleTrackChange(track.label)
  }

  trackIsMetadata(track) {
    return track.label == 'segment-metadata';
  }

  trackIsAdCues(track) {
    return track.label == 'ad-cues';
  }

  trackHasProperKind(track) {
    const enable_from_master_playlist = isFeatureToggleOn('enable_subtitle_from_master_playlist');
    const properKind = enable_from_master_playlist ? "subtitles" : "captions";
    return track.kind === properKind;
  }

  handleTrackChange(trackLabel) {
    for (const t of this.textTracks()) {
      if (t.label === trackLabel && this.trackHasProperKind(t)) {
        if (t.mode === 'disabled' || t.mode === 'hidden') {
          this.setPreferredLang(trackLabel);
          this.setActiveSubtitle(trackLabel.toLowerCase());
          t.mode = 'showing';
        }

      // If this text track is not the component's track and it is not
      // disabled, set it to disabled.
      } else if (t.mode === 'showing') {
        t.mode = 'disabled';
      }
    }
  }

  setPreferredSub() {
    let preferredLang = this.getPreferredLang();
    
    this.setInitialPreferredSubtitle();

    for (const t of this.textTracks()) {
      if (this.trackHasProperKind(t) && (t.label.toLowerCase() === preferredLang)) {
        t.mode = 'showing'
      } else {
        t.mode = 'disabled'
      }
    }
  }

  setInitialPreferredSubtitle() {
    let preferredLang = this.preferredLang();

    const availableSubs = this.textTracks().map((it) => {
      return it.label.toLowerCase();
    })

    let initialSub = 'none'

    if (availableSubs.indexOf(preferredLang) !== -1) {
      initialSub = preferredLang
    }
    
    const playerId = this.player.id();

    if (window["playback_initial_subtitle"] && typeof window['playback_initial_subtitle'] === 'object') {
      window["playback_initial_subtitle"][playerId] = initialSub;
    } else {
      window["playback_initial_subtitle"] = {
        [playerId]: initialSub,
      };
    }
    this.setActiveSubtitle(initialSub)

  }

  getPreferredLang() {
    let preferredLang = this.preferredLang();

    const availableSubs = this.textTracks().map((it) => {
      return it.label.toLowerCase();
    })

    if (preferredLang === 'off') {
      return 'off';
    }

    if (availableSubs.indexOf(preferredLang) !== -1) {
      return preferredLang;
    } else if(availableSubs.indexOf(this.defaultLang) !== -1) {
      return this.defaultLang;
    } else if (availableSubs.length > 0) {
      return availableSubs[0];
    } 

    return this.defaultLang;
  }

  setPreferredLang(lang) {
    localStorageAdapter().setItem(this.storageName(), lang.toLowerCase());
  }

  handleSubtitleOff() {
    for (const t of this.textTracks()) {
      this.setPreferredLang('off');
      this.setActiveSubtitle("off");

      if (t.mode === 'showing') {
        t.mode = 'disabled';
      }
    }
  }
}